import React from 'react';
import { useQuery } from 'react-query';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Activity,
  AlertCircle,
  Bot,
  Briefcase,
  ClipboardCheck,
  CreditCard,
  Mail,
  ShieldCheck,
  UsersRound,
  ChevronDown,
  ChevronRight,
  Plus
} from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import { adminAPI } from '../../services/api';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const CHART_COLORS = [
  '#dbeafe', // blue-100
  '#93c5fd', // blue-300
  '#60a5fa', // blue-400
  '#3b82f6', // blue-500
  '#1d4ed8'  // blue-700
];

const formatNumber = (value) => (
  new Intl.NumberFormat('en-US', {
    notation: Math.abs(Number(value) || 0) >= 10000 ? 'compact' : 'standard'
  }).format(Number(value) || 0)
);

const formatMoney = (cents, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: String(currency || 'USD').trim().toUpperCase(),
      maximumFractionDigits: 0
    }).format((Number(cents) || 0) / 100);
  } catch (_error) {
    return `${String(currency || '').trim().toUpperCase()} ${formatNumber((Number(cents) || 0) / 100)}`.trim();
  }
};

const relativeTime = (value) => {
  if (!value) return 'No timestamp';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No timestamp';

  const diff = Date.now() - date.getTime();
  const absoluteDiff = Math.abs(diff);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const suffix = diff < 0 ? 'from now' : 'ago';

  if (absoluteDiff < minute) return 'Just now';
  if (absoluteDiff < hour) return `${Math.floor(absoluteDiff / minute)}m ${suffix}`;
  if (absoluteDiff < day) return `${Math.floor(absoluteDiff / hour)}h ${suffix}`;
  return `${Math.floor(absoluteDiff / day)}d ${suffix}`;
};

const labelize = (value) => (
  String(value || 'Unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\bAi\b/g, 'AI')
    .replace(/\bApi\b/g, 'API')
    .replace(/\bHr\b/g, 'HR')
    .replace(/\bKyc\b/g, 'KYC')
    .replace(/\bRbac\b/g, 'RBAC')
);

const toNumber = (value) => Number(value || 0);
const asArray = (value) => (Array.isArray(value) ? value : []);

const getErrorMessage = (error) => (
  error?.response?.data?.error || error?.message || 'The dashboard request could not be completed.'
);

const normalizeDistribution = (items = [], valueKey = 'count') => (
  asArray(items)
    .map((item) => ({
      label: item.label || labelize(item.status || item.role || item.currency),
      value: toNumber(item[valueKey]),
      raw: item
    }))
    .filter((item) => item.value > 0)
);

const countByStatus = (items = [], statuses = []) => {
  const wanted = new Set(statuses.map((status) => String(status).toLowerCase()));
  return asArray(items).reduce((sum, item) => {
    const key = String(item.status || item.role || item.label || '').toLowerCase();
    return wanted.has(key) ? sum + toNumber(item.count) : sum;
  }, 0);
};

const DashboardSkeleton = () => (
  <div className="min-h-full bg-[#f8f9fa] p-4 sm:p-6 lg:p-8 font-sans">
    <div className="mx-auto max-w-[1400px]">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 flex flex-col gap-6">
          <SkeletonBlock className="h-80 rounded-[24px]" />
          <div className="grid gap-6 md:grid-cols-3">
            <SkeletonBlock className="h-40 rounded-[24px]" />
            <SkeletonBlock className="md:col-span-2 h-40 rounded-[24px]" />
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            <SkeletonBlock className="h-72 rounded-[24px]" />
            <SkeletonBlock className="h-72 rounded-[24px]" />
            <SkeletonBlock className="h-72 rounded-[24px]" />
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <SkeletonBlock className="h-[320px] rounded-[24px]" />
          <SkeletonBlock className="flex-1 rounded-[24px] min-h-[400px]" />
        </div>
      </div>
    </div>
  </div>
);

function MetricBarChart({ rows = [], emptyLabel, ariaLabel }) {
  const cleanRows = asArray(rows).filter((row) => toNumber(row.value) > 0);

  if (!cleanRows.length) {
    return (
      <div className="h-full flex items-center justify-center border border-dashed border-slate-200 rounded-xl bg-slate-50">
        <span className="text-sm text-slate-500 font-medium">{emptyLabel}</span>
      </div>
    );
  }

  const data = {
    labels: cleanRows.map((row) => row.label),
    datasets: [{
      label: 'Count',
      data: cleanRows.map((row) => toNumber(row.value)),
      backgroundColor: cleanRows.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]),
      borderRadius: 4,
      borderSkipped: false,
      maxBarThickness: 32
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: { color: '#64748b', font: { family: 'inherit' } },
        grid: { display: false, drawBorder: false }
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, color: '#64748b', font: { family: 'inherit' } },
        grid: { color: 'rgba(100, 116, 139, 0.1)', drawBorder: false }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.94)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        titleColor: '#fff',
        bodyColor: '#e2e8f0',
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context) => `${formatNumber(context.parsed.y)}`
        }
      }
    }
  };

  return <Bar data={data} options={options} role="img" aria-label={ariaLabel} />;
}

function DoughnutDistribution({ items = [], emptyLabel, ariaLabel }) {
  const rows = normalizeDistribution(items, 'count');

  if (!rows.length) {
    return (
      <div className="h-full flex items-center justify-center border border-dashed border-slate-200 rounded-xl bg-slate-50">
        <span className="text-sm text-slate-500 font-medium">{emptyLabel}</span>
      </div>
    );
  }

  const data = {
    labels: rows.map((item) => item.label),
    datasets: [{
      data: rows.map((item) => item.value),
      backgroundColor: rows.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]),
      borderWidth: 0,
      cutout: '75%'
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.94)',
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context) => `${context.label}: ${formatNumber(context.parsed)}`
        }
      }
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-1 min-h-[140px] relative">
         <Doughnut data={data} options={options} role="img" aria-label={ariaLabel} />
      </div>
      <div className="mt-4 flex flex-col gap-2.5">
        {rows.slice(0, 3).map((item, i) => (
          <div key={item.label} className="flex justify-between items-center text-xs">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
              <span className="text-slate-500">{item.label}</span>
            </div>
            <span className="font-semibold text-slate-900">{formatNumber(item.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GaugeChart({ value }) {
  const data = {
    datasets: [{
      data: [value, 100 - value],
      backgroundColor: ['#3b82f6', '#f1f5f9'],
      borderWidth: 0,
      cutout: '80%',
      circumference: 180,
      rotation: 270
    }]
  };
  return (
    <div className="relative w-full aspect-[2/1] mt-auto">
      <Doughnut data={data} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { enabled: false } } }} />
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-1 sm:pb-3">
        <span className="text-3xl font-bold text-slate-900 tracking-tight">{value}%</span>
      </div>
    </div>
  );
}

export default function DashboardOverview() {
  const {
    data,
    error,
    isError,
    isLoading,
    refetch
  } = useQuery('admin-dashboard-overview', adminAPI.getOverview, {
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true
  });

  if (isLoading) return <DashboardSkeleton />;

  if (isError) {
    return (
      <div className="min-h-full bg-[#f8f9fa] flex items-center justify-center p-6">
        <div className="rounded-2xl border border-rose-200 bg-white p-6 shadow-sm max-w-md w-full text-center">
          <AlertCircle className="h-10 w-10 text-rose-500 mx-auto mb-4" />
          <h1 className="font-semibold text-slate-900 text-lg">Dashboard Unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">{getErrorMessage(error)}</p>
          <button
            onClick={() => refetch()}
            className="mt-6 inline-flex items-center rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const dashboard = data || {};
  const summary = dashboard.summary || {};
  const health = dashboard.health || {};
  const aiUsage = dashboard.aiUsage || {};
  const billing = dashboard.billing || {};

  const revenueByCurrency = asArray(billing.revenueByCurrency);
  const primaryRevenue = revenueByCurrency[0];
  const aiCalls = toNumber(aiUsage.calls30d);
  const aiFailureRate = toNumber(health.aiFailureRatePercent);
  const emailPending = countByStatus(dashboard.emailQueue, ['pending', 'queued', 'processing', 'retrying']);
  const accountsOther = Math.max(
    toNumber(summary.totalAccounts) -
      toNumber(summary.candidateAccounts) -
      toNumber(summary.employerAccounts) -
      toNumber(summary.adminAccounts),
    0
  );

  const revenueChartRows = revenueByCurrency.map((revenue) => ({
    label: revenue.currency,
    revenueMajor: toNumber(revenue.revenueCents30d) / 100
  }));

  // Fallback to active jobs chart if no revenue is recorded
  const mainChartData = revenueChartRows.length > 0 
    ? revenueChartRows.map(r => ({ label: r.label, value: r.revenueMajor }))
    : [
        { label: 'Interviews', value: summary.interviews },
        { label: 'Upcoming', value: summary.upcomingInterviews },
        { label: 'Offers', value: summary.offers },
        { label: 'Open', value: summary.openOffers }
      ];

  const mainChartLabel = revenueChartRows.length > 0 ? "Revenue by currency" : "Hiring movement";

  return (
    <div className="min-h-full bg-[#f8f9fa] p-4 sm:p-6 lg:p-8 font-sans">
      <div className="mx-auto max-w-[1400px]">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 relative z-10">
          
          {/* Main Left Area (Cols 1-3) */}
          <div className="lg:col-span-3 flex flex-col gap-6">
            
            {/* ROW 1: Pipeline / Revenue Overview */}
            <div className="bg-white rounded-[24px] shadow-sm flex flex-col md:flex-row overflow-hidden border border-slate-100">
              {/* Left Chart Area */}
              <div className="flex-1 p-6 sm:p-8 flex flex-col">
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">
                      {primaryRevenue ? formatMoney(primaryRevenue.revenueCents30d, primaryRevenue.currency) : formatNumber(summary.totalAccounts)}
                    </h2>
                    <p className="text-sm font-medium text-slate-500 mt-1">
                      {primaryRevenue ? 'Total Revenue (30d)' : 'Total Platform Accounts'}
                    </p>
                  </div>
                  <div className="flex bg-slate-50 rounded-lg p-1">
                    <button className="px-3 py-1.5 rounded-md bg-white shadow-sm text-xs font-semibold text-slate-900">30d</button>
                    <button className="px-3 py-1.5 rounded-md text-xs font-medium text-slate-500 hover:text-slate-900">Y</button>
                  </div>
                </div>
                <div className="h-56 w-full mt-auto">
                  <MetricBarChart
                    rows={mainChartData}
                    emptyLabel="No chart data available."
                    ariaLabel={mainChartLabel}
                  />
                </div>
              </div>
              
              {/* Right Stats Area */}
              <div className="w-full md:w-64 border-t md:border-t-0 md:border-l border-slate-100 p-6 sm:p-8 flex flex-col justify-center gap-8 bg-slate-50/50">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Jobs</p>
                  <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">{formatNumber(summary.activeJobs)}</p>
                  <p className="text-xs font-medium text-blue-500 mt-1">↑ Current postings</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Accounts</p>
                  <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">{formatNumber(summary.totalAccounts)}</p>
                  <p className="text-xs font-medium text-emerald-500 mt-1">Total registered</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">KYC Queue</p>
                  <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">{formatNumber(summary.openKycQueue)}</p>
                  <p className="text-xs font-medium text-amber-500 mt-1">Awaiting review</p>
                </div>
              </div>
            </div>

            {/* ROW 2: Middle cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* AI Health Bar */}
              <div className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col justify-between h-40">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">AI Success Rate</h3>
                  <p className="text-xs text-slate-400 mt-1">Out of {formatNumber(aiCalls)} calls</p>
                </div>
                <div>
                  <div className="flex justify-between items-end mb-2">
                    <span className="text-lg font-bold text-blue-600">{100 - aiFailureRate}%</span>
                    <span className="text-[10px] font-semibold text-slate-300 uppercase tracking-wider">Target 100%</span>
                  </div>
                  <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all duration-1000" style={{ width: `${100 - aiFailureRate}%` }} />
                  </div>
                </div>
              </div>

              {/* System Attention (Optimize Tips) */}
              <div className="md:col-span-2 bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex items-center justify-between">
                <div className="max-w-[280px]">
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900">System Attention Needed</h3>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                    You have <strong className="font-semibold text-slate-700">{formatNumber(summary.openKycQueue)}</strong> KYC reviews pending and <strong className="font-semibold text-slate-700">{formatNumber(emailPending)}</strong> emails queued for delivery.
                  </p>
                  <button className="inline-block mt-4 text-xs font-semibold text-slate-900 hover:text-blue-600 transition-colors">Review queues &gt;</button>
                </div>
                <div className="hidden sm:flex items-end gap-1.5 h-16 opacity-80">
                  <div className="w-5 bg-blue-100 rounded-t-sm" style={{ height: '30%' }}></div>
                  <div className="w-5 bg-blue-200 rounded-t-sm" style={{ height: '50%' }}></div>
                  <div className="w-5 bg-blue-300 rounded-t-sm" style={{ height: '70%' }}></div>
                  <div className="w-5 bg-blue-500 rounded-t-sm" style={{ height: '100%' }}></div>
                </div>
              </div>
            </div>

            {/* ROW 3: Bottom cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {/* Account Mix (Cost analysis) */}
              <div className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Account Mix</h3>
                    <p className="text-xs text-slate-400 mt-1">Platform demographics</p>
                  </div>
                  <button className="bg-slate-50 border border-slate-100 text-slate-600 px-2.5 py-1 rounded-md text-xs font-medium">All</button>
                </div>
                <DoughnutDistribution
                  items={[
                    { label: 'Candidates', count: summary.candidateAccounts },
                    { label: 'Employers', count: summary.employerAccounts },
                    { label: 'Admins', count: summary.adminAccounts },
                    { label: 'Other', count: accountsOther }
                  ]}
                  emptyLabel="No accounts."
                  ariaLabel="Account mix distribution"
                />
              </div>

              {/* System Health Gauge */}
              <div className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col items-center">
                <div className="w-full flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">System Health</h3>
                    <p className="text-xs text-slate-400 mt-1">Performance status</p>
                  </div>
                  <button className="bg-slate-50 border border-slate-100 text-slate-600 px-2.5 py-1 rounded-md text-xs font-medium">Now</button>
                </div>
                <div className="w-full text-left mb-4">
                  <h4 className="text-2xl font-bold text-slate-900">{100 - aiFailureRate}%</h4>
                  <p className="text-xs font-medium text-emerald-500 mt-1">↑ Operational</p>
                </div>
                <GaugeChart value={Math.round(100 - aiFailureRate)} />
                <p className="text-center text-[11px] text-slate-400 mt-5 leading-tight px-2">Based on API latency and AI success rates</p>
              </div>

              {/* Job Statuses (Goal Tracker) */}
              <div className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-sm font-semibold text-slate-900">Job Statuses</h3>
                  <button className="text-xs font-semibold text-slate-900 flex items-center gap-1 hover:text-blue-600 transition-colors"><Plus className="w-3 h-3" /> View all</button>
                </div>
                <div className="flex flex-col gap-5 mt-2">
                  <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Breakdown</div>
                  {normalizeDistribution(dashboard.jobStatuses, 'count').slice(0, 4).map((item, i) => (
                    <div key={i} className="flex flex-col gap-1.5">
                      <div className="flex justify-between items-end text-xs">
                        <span className="font-semibold text-slate-700">{item.label}</span>
                        <span className="text-slate-400 font-medium">{formatNumber(item.value)}</span>
                      </div>
                      <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${Math.min(100, (item.value / Math.max(1, summary.jobPostings)) * 100)}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      </div>
                    </div>
                  ))}
                  {normalizeDistribution(dashboard.jobStatuses, 'count').length === 0 && (
                    <div className="text-sm text-slate-500 text-center py-4">No jobs found</div>
                  )}
                </div>
              </div>
            </div>

          </div>

          {/* Right Area (Col 4) */}
          <div className="flex flex-col gap-6">
            
            {/* Admin Premium Card */}
            <div className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100">
              <div className="flex justify-between items-center mb-5">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Admin Control</h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">Quick access</p>
                </div>
              </div>
              
              {/* Credit Card Graphic */}
              <div className="rounded-[16px] bg-gradient-to-br from-blue-500 to-blue-700 p-5 text-white shadow-lg shadow-blue-500/20 relative overflow-hidden h-40 flex flex-col justify-between hover:scale-[1.02] transition-transform cursor-pointer">
                <div className="absolute top-0 right-0 p-4 opacity-20">
                  <ShieldCheck className="w-16 h-16" />
                </div>
                <div className="relative z-10 flex justify-between items-start">
                  <span className="text-xs font-medium opacity-80">Dashboard</span>
                  <span className="text-sm font-bold tracking-widest">SYS</span>
                </div>
                <div className="relative z-10">
                  <p className="font-mono text-lg tracking-widest opacity-90 mb-1">ADMIN-102</p>
                  <div className="flex justify-between items-end">
                    <span className="text-sm font-semibold truncate pr-4 uppercase">System Admin</span>
                    <span className="text-xs opacity-80 shrink-0">ON</span>
                  </div>
                </div>
              </div>

              {/* Quick Action Icons */}
              <div className="flex justify-between items-center mt-6 px-1 border-b border-slate-100 pb-6">
                {[
                  { icon: ClipboardCheck, label: 'KYC' },
                  { icon: UsersRound, label: 'Users' },
                  { icon: Briefcase, label: 'Jobs' },
                  { icon: Bot, label: 'AI' }
                ].map((action, i) => (
                  <div key={i} className="flex flex-col items-center gap-2 cursor-pointer group">
                    <div className="w-12 h-12 rounded-2xl border border-slate-100 bg-white shadow-sm flex items-center justify-center text-slate-600 group-hover:bg-slate-50 group-hover:text-blue-600 transition-colors">
                      <action.icon className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] font-semibold text-slate-500">{action.label}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">System Checks</h4>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                   <div className="flex flex-col items-center gap-1.5 shrink-0 w-14 group text-center cursor-pointer">
                     <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-50 transition-colors shrink-0">
                       <Mail className="w-4 h-4" />
                     </div>
                     <span className="text-[10px] font-medium text-slate-600 truncate w-full">Emails</span>
                   </div>
                   <div className="flex flex-col items-center gap-1.5 shrink-0 w-14 group text-center cursor-pointer">
                     <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-50 transition-colors shrink-0">
                       <CreditCard className="w-4 h-4" />
                     </div>
                     <span className="text-[10px] font-medium text-slate-600 truncate w-full">Billing</span>
                   </div>
                   <div className="flex flex-col items-center gap-1.5 shrink-0 w-14 group text-center cursor-pointer">
                     <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-50 transition-colors shrink-0">
                       <AlertCircle className="w-4 h-4" />
                     </div>
                     <span className="text-[10px] font-medium text-slate-600 truncate w-full">Alerts</span>
                   </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex-1 min-h-[300px]">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-sm font-semibold text-slate-900">Recent activity</h3>
                <button className="bg-slate-50 border border-slate-100 text-slate-600 px-2 py-1 rounded-md text-[11px] font-medium flex items-center gap-1 hover:bg-slate-100">Live <ChevronDown className="w-3 h-3" /></button>
              </div>
              
              <div className="flex justify-between text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-4 px-1">
                <span>Event</span>
                <span>Time</span>
              </div>

              <div className="flex flex-col gap-5">
                {asArray(dashboard.recentActivity).length ? asArray(dashboard.recentActivity).slice(0, 6).map((item, i) => {
                  return (
                    <div key={i} className="flex items-center justify-between group cursor-pointer hover:bg-slate-50 p-2 -mx-2 rounded-xl transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0 ${['bg-emerald-500', 'bg-blue-500', 'bg-slate-800', 'bg-sky-500', 'bg-amber-500'][i % 5]}`}>
                          <Activity className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 pr-3">
                          <p className="text-sm font-semibold text-slate-900 truncate">{labelize(item.action)}</p>
                          <p className="text-xs text-slate-500 truncate">{labelize(item.resourceType || 'system')}</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] font-medium text-slate-400">{relativeTime(item.createdAt)}</p>
                      </div>
                    </div>
                  );
                }) : (
                  <div className="text-center py-8 flex flex-col items-center border border-dashed border-slate-200 rounded-xl">
                    <Activity className="w-6 h-6 text-slate-300 mb-2" />
                    <p className="text-xs font-medium text-slate-500">No recent activity</p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
